import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from  'lodash/values';
import forEach from  'lodash/forEach';
import find from 'lodash/find';
import pick from 'lodash/pick';
import _get from 'lodash/get';
import { SubmissionError } from 'redux-form';
import http from '../shared/http';
import endpoints from '../../config/endpoints';
import intl from '../shared/intl';
import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection
} from '../shared/form-elements/select';
import checkPermissions from '../shared/check-permissions';

// actions types
const FETCH_DATA_SUCCESS = 'payout-systems/fetch-data-success';
const FETCH_DATA_ERROR = 'payout-systems/fetch-data-error';

const FETCH_TRANSLATION_SUCCESS = 'payout-systems/fetch-translation-success';
const FETCH_TRANSLATION_ERROR = 'payout-systems/fetch-translation-error';

const UPDATE_TRANSLATION_SUCCESS = 'payout-systems/update-translation-success';
const UPDATE_TRANSLATION_ERROR = 'payout-systems/update-translation-error';

const SUBMIT_SUCCESS = 'payout-systems/submit-success';
const SUBMIT_ERROR = 'payout-systems/submit-error';

const REMOVE_SUCCESS = 'payout-systems/remove-success';
const REMOVE_ERROR = 'payout-systems/remove-error';

const SORT_BY_COLUMN = 'payout-systems/table-sort-by-column';
const SHOW_MORE_ROWS = 'payout-systems/table-show-more-rows';

const SWITCH_LANG = 'payout-systems/switch-lang';

const TOGGLE_PROGRESS_BAR = 'payout-systems/toggle-progress-bar';

const UPLOAD_SUCCESS = 'payout-systems/upload-success';
const UPLOAD_ERROR = 'payout-systems/upload-error';
const PREPARATION_FILE_SUCCESS = 'payout-systems/preparation-file-success';

// permissions
export const allowedPermissions = {
	'view': 'payout_systems_list',
	'create': 'payout_systems_create',
	'edit': 'payout_systems_update',
	'delete': 'payout_systems_delete'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'Id',
				direction: 'desc'
			},
			title: {
				index: 'title',
				title: 'Title',
				direction: 'desc'
			},
			status: {
				index: 'status',
				title: 'Status',
				direction: 'desc'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				direction: 'desc'
			},
			updated_at: {
				index: 'updated_at',
				title: 'Updated',
				direction: 'desc'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'id',
			title: 'Id',
			direction: 'desc'
		},
		rowsNumber: 1000
	},
	defaultLanguage: 'ru',
	currentLanguage: 'ru',
	form: {
		title: '',
		category: '',
		region: '',
		currency: '',
		type: '',
		status: '',
		fields: '',
		min_amount: 0,
		payout_percent: 0,
		payout_fee: 0,
		module: '',
		module_options: '',
		allow_floats: false,
		is_auto_approve_enabled: true
	},
	translationForm: {
		title: ''
	},
	error: false
};

// actions
export const actions = {
	fetchData: () => dispatch => {
		(http({
			url: `${endpoints.PAYOUT_SYSTEMS.LIST}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},

	submitForm: (formData, id = null) => (dispatch, getState) => {
		let fields = formData.fields;
		try {
			fields = JSON.parse(fields);
		} catch (e) {
			fields = null;
		}

		let module_options = formData.module_options;
		try {
			module_options = JSON.parse(module_options);
		} catch (e) {
			module_options = null;
		}
		const request = http({
			url: !id ? endpoints.PAYOUT_SYSTEMS.LIST : `${endpoints.PAYOUT_SYSTEMS.LIST}/${id}`,
			method: !id ? 'post' : 'put',
			actions: [
				SUBMIT_SUCCESS,
				SUBMIT_ERROR
			],
			data: pick({
				...getState().payoutSystems.form,
				...formData,
				status: formData.status && formData.status.length && formData.status[0].value || '',
				category: formData.category && formData.category.length && formData.category[0].value || '',
				region: formData.region && formData.region.length && formData.region[0].value || null,
				type: formData.type && formData.type.length && formData.type[0].value || '',
				currency: formData.currency && formData.currency.length && formData.currency[0].value || '',
				module: formData.module && formData.module.length && formData.module[0].value || null,
				fields,
				module_options,
				...getPayoutCommissions(formData),
				allow_floats: !!formData.allow_floats,
				is_auto_approve_enabled: !!formData.is_auto_approve_enabled
			}, Object.keys(getState().payoutSystems.form))
		});

		return request(dispatch)
			.then(action => {
				if (action.type === SUBMIT_SUCCESS) {
					const id = id || action.payload.data.id;
					const fileToUpload = getState().payoutSystems.fileToUpload;

					if (fileToUpload) {
						actions.uploadFile(id, fileToUpload, dispatch);
					} else {
						dispatch(actions.redirectToModuleIndex());
					}
				}

				if (action.type === SUBMIT_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	uploadFile: (id, fileToUpload, dispatch) => {
		const field = Array.from(fileToUpload.keys())[0]; // image || image2

		const request = http({
			url: `${endpoints.PAYOUT_SYSTEMS.LIST}/${id}/${field}`,
			method: 'put',
			actions: [
				UPLOAD_SUCCESS,
				UPLOAD_ERROR
			],
			data: fileToUpload
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch).then(action => {
			if (action.type === UPLOAD_SUCCESS) {
				dispatch(actions.redirectToModuleIndex());
			}
		});
	},

	preparationFile: (field = 'image', file, nextAction) => dispatch => {
		const reader = new FileReader();
		const data = new FormData();

		data.append(field, file);

		reader.readAsDataURL(file);
		reader.onloadend = function () {
			nextAction(reader.result);
			dispatch({
				type: PREPARATION_FILE_SUCCESS,
				payload: data
			});
		};
	},

	remove: id => dispatch => {
		const request = http({
			url: `${endpoints.PAYOUT_SYSTEMS.BASE}/${id}`,
			method: 'delete',
			actions: [
				REMOVE_SUCCESS,
				REMOVE_ERROR
			]
		});

		dispatch(actions.toggleProgressBar());

		request(dispatch);
	},

	sortByColumn: columnIndex => ({
		type: SORT_BY_COLUMN,
		payload: columnIndex
	}),

	showMoreRows: () => ({
		type: SHOW_MORE_ROWS
	}),

	switchLanguage: lang => (dispatch, getState) => {
		const [,, psId] = getState().routing.locationBeforeTransitions.pathname.split('/').filter(i => !!i);

		http({
			url: endpoints.PAYOUT_SYSTEMS.TRANSLATIONS
				.replace('{id}', psId)
				.replace('{lang}', lang),
			method: 'get',
			actions: [
				FETCH_TRANSLATION_SUCCESS,
				FETCH_TRANSLATION_ERROR
			]
		})(dispatch)
			.then(() => dispatch({ type: SWITCH_LANG, payload: lang }));
	},
	updateTranslations: formData => (dispatch, getState) => {
		const [,, psId] = getState().routing.locationBeforeTransitions.pathname.split('/').filter(i => !!i);
		const lang = getState().payoutSystems.currentLanguage;
		http({
			url: endpoints.PAYOUT_SYSTEMS.TRANSLATIONS
				.replace('{id}', psId)
				.replace('{lang}', lang),
			method: 'put',
			actions: [
				UPDATE_TRANSLATION_SUCCESS,
				UPDATE_TRANSLATION_ERROR
			],
			data: formData
		})(dispatch)
			.then(() => actions.switchLanguage(getState().payoutSystems.defaultLanguage)(dispatch, getState));
	},

	redirectToModuleIndex: () => dispatch => {
		// @todo Adhock for redux-form submit functionality.
		setTimeout(() => dispatch(replace('payout-systems/')), 100);
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	})
};

// reducers
export default handleActions({
	[LOCATION_CHANGE]: (state, action) => {
		if (
			action.payload.pathname.indexOf('payout-systems') !== -1 &&
			action.payload.pathname.indexOf('payout-systems/create') === -1
		) {
			return {
				...state,
				data: null
			};
		}

		return state;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state, data: action.payload.data, showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SORT_BY_COLUMN]: (state, action) => {
		const { sortByColumn, columns } = state.table;
		const columnIndex = action.payload;
		const direction = sortByColumn['index'] == columnIndex ?
			sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
			columns[columnIndex].direction;

		return {
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...columns[columnIndex],
					direction
				}
			}
		};
	},
	[SHOW_MORE_ROWS]: state => ({
		...state,
		table: {
			...state.table,
			rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
		}
	}),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[REMOVE_SUCCESS]: state => ({...state, data: null}),
	[REMOVE_ERROR]: state => ({...state, error: true}),
	[FETCH_TRANSLATION_SUCCESS]: (state, action) => ({
		...state, translationForm: {title: action.payload.data.title}
	}),
	[PREPARATION_FILE_SUCCESS]: (state, action) => ({...state, fileToUpload: action.payload}),
	[SWITCH_LANG]: (state, action) => ({...state, currentLanguage: action.payload}),
	[UPLOAD_SUCCESS]: state => ({...state, fileToUpload: null}),
	[SUBMIT_ERROR]: (state, action) => {
		const { response: {status = null, data = null} } = action.payload;
		if (status === 400 && !!data) return state;
		return { ...state, error: true };
	}
}, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutSystems.data,
	getTranslationFormInitialValues: state => state.payoutSystems.translationForm,
	getTable: state => {
		const { table, data } = state.payoutSystems;
		const rows = state.payoutSystems.data ? sortColumns(data, table.sortByColumn) : [];
		let partialData = cloneDeep(rows.slice(0, table.rowsNumber));
		partialData = partialData.map(row => {
			row.status = find(state.root.lists['payout-system-statuses'], {id: row.status}).text;
			return row;
		});
		return {
			headers: values(table.columns),
			rows: partialData,
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getStatusSelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['payout-system-statuses']),
	getCategorySelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['payout-system-categories']),
	getPayoutSystemRegions: state => createSelectOptionsFromSysEnums(state.root.lists['payout-system-regions']),
	getCurrencySelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['available-currencies']),
	getModuleSelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['payout-system-modules']),
	getTypeSelectOptions: state => createSelectOptionsFromSysEnums(state.root.lists['payout-system-types']),
	getIntl: state => intl(state),
	getProgressBar: state => state.payoutSystems.showProgressBar,
	getFieldsById: state => id => {
		const current = find(state.payoutSystems.data, { id: parseInt(id) });
		return {
			...state.payoutSystems.form,
			...current,
			module_options: current.module_options && JSON.stringify(current.module_options),
			status: createSelectValuesUseIntersection(
				selectors.getStatusSelectOptions(state),
				[current.status]
			),
			category: createSelectValuesUseIntersection(
				selectors.getCategorySelectOptions(state),
				[current.category]
			),
			region: createSelectValuesUseIntersection(
				selectors.getPayoutSystemRegions(state),
				[current.region]
			),
			payout_commission_type: createSelectValuesUseIntersection(
				selectors.getPayoutFeeTypeOptions(state),
				[ Number(current.payout_fee) ? 'number' : 'percent' ]
			),
			payout_commission: Number(current.payout_fee) || Number(current.payout_percent) || 0,
			module: createSelectValuesUseIntersection(
				selectors.getModuleSelectOptions(state),
				[current.module]
			),
			currency: createSelectValuesUseIntersection(
				selectors.getCurrencySelectOptions(state),
				[current.currency]
			),
			type: createSelectValuesUseIntersection(
				selectors.getTypeSelectOptions(state),
				[current.type]
			),
			fields: JSON.stringify(current.fields)
		};
	},
	getMainTabLangs: (state, formType) => {
		const { currentLanguage } = state.payoutSystems;
		return getTabLangs(formType, currentLanguage)(state);
	},
	getPayoutFeeTypeOptions: (state) => {
		return [
			{ label: 'percent', value: 'percent' },
			{ label: 'number', value: 'number' }
		];
	},
	getLangSwitchState: state => ({
		defaultLanguage: state.payoutSystems.defaultLanguage,
		currentLanguage: state.payoutSystems.currentLanguage
	}),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.payoutSystems.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => {
		const value = row[sortedColumn.index];
		switch (sortedColumn.index) {
			case 'id':
			case 'title':
			case 'status':
			case 'created_at':
			case 'updated_at':
				return value;
			default:
				return value;
		}
	}, sortedColumn.direction);
};

// function
function getTabLangs(formType, currentLanguage) {
	return state => {
		const { defaultLanguage } = state.payoutSystems;

		let [en, ru, ...langs] = state.root.lists.languages;
		if (formType === 'create') {
			return [{ ...ru, isSelected: true, isActive: true, isDefault: true }];
		}
		return [ru, en, ...langs].map(lang => ({
			...lang,
			isSelected: true,
			isActive: lang.id === currentLanguage,
			isDefault: lang.id === defaultLanguage
		}));
	};
}

function getPayoutCommissions(formData) {
	const payoutFeeType = _get(formData, 'payout_commission_type.0.value', 'percent');

	const payout_percent = payoutFeeType === 'percent' ? formData.payout_commission : 0;
	const payout_fee = payoutFeeType === 'number' ? formData.payout_commission : 0;

	return { payout_percent, payout_fee };
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-systems/index.js